<script lang="ts">
	import type { LayerName } from '$lib/components/map/map-styles';
	import type { MainPoiCategory } from '$lib/components/pois/types';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import CompassControl from '$lib/components/map/mapTools/controls/compass/CompassControl.svelte';
	import LocationControl from '$lib/components/map/mapTools/controls/location/LocationControl.svelte';
	import LoginButton from '$lib/components/map/mapTools/controls/login/LoginButton.svelte';
	import MapSettingsButton from '$lib/components/map/mapTools/controls/settings/MapSettingsButton.svelte';
	import ZoomControls from '$lib/components/map/mapTools/controls/zoom/ZoomControls.svelte';
	import LayerSelector from '$lib/components/map/mapTools/settings/layerSelector/LayerSelector.svelte';
	import PoiCategorySettingsCard from '$lib/components/map/mapTools/settings/poiCategorySelector/PoiCategorySettingsCard.svelte';
	import SettingsCard from '$lib/components/map/mapTools/settings/settingsCard/SettingsCard.svelte';
	import TrafficInfoSettingsCard from '$lib/components/map/mapTools/settings/settingsCard/TrafficInfoSettingsCard.svelte';
	import LowEmissionZoneSwitch from '$lib/components/map/mapTools/switches/LowEmissionZoneSwitch.svelte';
	import * as m from '$paraglide/messages';
	import { onMount } from 'svelte';
	import { getMapContext } from 'svelte-maplibre';

	interface Props {
		selectedLayerName: LayerName;
		selectedPoiCategories: MainPoiCategory[];
		isTrafficPage: boolean;
		withPartner?: boolean;
		showPoiCategorySelector?: boolean;
		onGetLocation: () => void;
		onPoiCategoriesChange: (categories: MainPoiCategory[]) => void;
		onLayerChange: () => void;
	}

	let {
		selectedLayerName = $bindable(),
		selectedPoiCategories,
		isTrafficPage,
		withPartner = false,
		showPoiCategorySelector = true,
		onGetLocation,
		onPoiCategoriesChange,
		onLayerChange,
	}: Props = $props();
	const { poiMarkerWithDetailsEnabled } = getMapLayoutContext();

	const { map } = $derived(getMapContext());

	onMount(async () => {
		// svelte-maplibre does not allow to deactivate pitch on mount so we do it manually here
		map?.setMaxPitch(0);
	});
</script>

<div
	class="absolute right-2 top-2 z-0 flex flex-col flex-shrink-0 justify-start items-end gap-2 w-full max-w-15 z-hover"
>
	<LoginButton />
	<MapSettingsButton>
		<SettingsCard>
			{#snippet title()}
				{m.layers__header()}
			{/snippet}
			<LayerSelector bind:selectedLayerName {onLayerChange} />
			<div class="py-2">
				<LowEmissionZoneSwitch />
			</div>
		</SettingsCard>
		<!--	Here selectedPoiCategories should never be undefined according to its type. On the
		itinerary results page it is. As the PoiCategorySettingsCard will disappear from MapTools,
		we'll condition its display in the meantime to fix the bug it causes.	-->
		{#if selectedPoiCategories}
			<PoiCategorySettingsCard
				{showPoiCategorySelector}
				{selectedPoiCategories}
				detailedView={$poiMarkerWithDetailsEnabled}
				{onPoiCategoriesChange}
				{withPartner}
			/>
		{/if}
		<TrafficInfoSettingsCard {isTrafficPage} />
	</MapSettingsButton>
	<div class="hidden md:block">
		<ZoomControls />
	</div>
	<LocationControl {onGetLocation} />
	<CompassControl />
</div>
